<template>
  <div class="booking-calendar">
    <!-- Desktop: Side by Side Layout -->
    <div class="lg:grid lg:grid-cols-2 lg:gap-6">
      <!-- Calendar Section -->
      <div
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
        class="touch-pan-y"
      >
        <!-- Month Navigation -->
        <div class="flex items-center justify-between mb-6">
          <button
            @click="previousMonth"
            :disabled="!canGoToPreviousMonth"
            class="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon class="w-5 h-5" />
          </button>
          
          <h3 class="text-lg font-semibold text-gray-900">
            {{ currentMonth.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }) }}
          </h3>
          
          <button
            @click="nextMonth"
            :disabled="!canGoToNextMonth"
            class="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon class="w-5 h-5" />
          </button>
        </div>

        <!-- Calendar Grid -->
        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <!-- Weekday Headers -->
      <div class="grid grid-cols-7 bg-gray-50">
        <div 
          v-for="day in weekdays" 
          :key="day"
          class="p-3 text-center text-sm font-medium text-gray-500"
        >
          {{ day }}
        </div>
      </div>

      <!-- Calendar Days -->
      <div class="grid grid-cols-7">
        <button
          v-for="day in calendarDays"
          :key="`${day.date.getTime()}`"
          @click="selectDate(day.date)"
          :disabled="!day.isSelectable"
          :class="[
            'p-3 text-sm border-r border-b border-gray-100 hover:bg-amber-50 transition-colors',
            'min-h-[60px] flex flex-col items-center justify-center',
            dayStatusClass(day),
            {
              'text-gray-300': !day.isCurrentMonth,
              'text-gray-900': day.isCurrentMonth && !day.isSelected && day.isSelectable,
              'bg-amber-500 text-white hover:bg-amber-600': day.isSelected,
              'text-gray-400 cursor-not-allowed': !day.isSelectable,
            }
          ]"
        >
          <span class="font-medium">{{ day.date.getDate() }}</span>
        </button>
      </div>
    </div>
      </div>

      <!-- Time Slots Section -->
      <div>
        <!-- Selected Date Info -->
        <div ref="timeSlotsSection" v-if="selectedDate" class="lg:mt-0 mt-6 p-4 bg-amber-50 rounded-lg">
      <h4 class="font-medium text-amber-900 mb-2">
        Geselecteerde datum: {{ selectedDate.toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }}
      </h4>
      
      <!-- Time Slots -->
      <div v-if="loadingSlots" class="flex items-center justify-center py-4">
        <LoadingSpinner size="sm" />
        <span class="ml-2 text-sm text-gray-600">Tijdslots laden...</span>
      </div>
      
      <!-- Flexible mode: pick your own whole-hour start/end (loyalty perk) -->
      <div v-else-if="flexible" class="space-y-3">
        <div v-if="!flexInfo || !flexInfo.available || flexInfo.free_hours.length === 0" class="text-center py-4">
          <p class="text-sm text-gray-600">Geen vrije uren op deze datum.</p>
          <p class="text-xs text-gray-500 mt-1">Probeer een andere datum.</p>
        </div>
        <template v-else>
          <p class="text-sm text-amber-800">
            Kies je start- en einduur ({{ flexInfo.hourly_rate_formatted }} per uur):
          </p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-amber-900 mb-1">Startuur</label>
              <select
                v-model.number="flexStart"
                class="w-full border-amber-200 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500"
              >
                <option :value="null" disabled>Kies startuur</option>
                <option v-for="h in flexInfo.free_hours" :key="`s-${h}`" :value="h">
                  {{ String(h).padStart(2, '0') }}:00
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-amber-900 mb-1">Einduur</label>
              <select
                v-model.number="flexEnd"
                :disabled="flexStart === null"
                class="w-full border-amber-200 rounded-lg text-sm focus:ring-amber-500 focus:border-amber-500 disabled:opacity-50"
              >
                <option :value="null" disabled>Kies einduur</option>
                <option v-for="h in flexEndOptions" :key="`e-${h}`" :value="h">
                  {{ String(h).padStart(2, '0') }}:00
                </option>
              </select>
            </div>
          </div>
          <div v-if="flexStart !== null && flexEnd !== null" class="text-sm font-medium text-amber-900">
            {{ flexEnd - flexStart }} uur × {{ flexInfo.hourly_rate_formatted }} = {{ flexTotalFormatted }}
          </div>
        </template>
      </div>

      <div v-else-if="availableSlots.length > 0" class="space-y-2">
        <p class="text-sm text-amber-800 mb-3">Beschikbare tijden voor {{ durationHours }} uur sessie:</p>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <button
            v-for="slot in availableSlots"
            :key="slot.start_time"
            @click="selectTimeSlot(slot)"
            :class="[
              'p-3 text-sm font-medium rounded-lg border transition-colors',
              selectedSlot?.start_time === slot.start_time
                ? 'bg-amber-500 text-white border-amber-500'
                : 'bg-white text-amber-600 border-amber-200 hover:bg-amber-50'
            ]"
          >
            {{ formatTime(slot.start_time) }} - {{ formatTime(slot.end_time) }}
          </button>
        </div>
      </div>
      
      <div v-else class="text-center py-4">
        <p class="text-sm text-gray-600">
          Geen tijdslots beschikbaar voor {{ durationHours }} uur op deze datum.
        </p>
        <p class="text-xs text-gray-500 mt-1">
          Probeer een andere datum of kortere sessie.
        </p>
      </div>
        </div>

        <!-- Error Message -->
        <ErrorMessage
          v-if="error"
          :message="error"
          @dismiss="error = null"
          :dismissible="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline'
import { useApi } from '@/composables/useApi'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ErrorMessage from '@/components/ErrorMessage.vue'
import type { TimeSlot } from '@/types'

interface Props {
  durationHours: number
  customerType?: 'individual' | 'professional'
  // Flexible mode (loyalty perk): user picks whole-hour start/end at a fixed
  // hourly rate instead of choosing from fixed-duration slots
  flexible?: boolean
  // Preselection when the visitor returns via a recovery link: the date
  // (Y-m-d) and, if still available, the slot that starts at this time
  initialDate?: string
  initialStartTime?: string
}

interface FlexDayInfo {
  available: boolean
  date: string
  open_time: string | null
  close_time: string | null
  free_hours: number[]
  hourly_rate_cents: number | null
  hourly_rate_formatted?: string
}

type DayStatus = 'free' | 'moderate' | 'limited' | 'full' | ''

interface CalendarDay {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  isSelected: boolean
  isSelectable: boolean
  availableSlots: number
  status: DayStatus
}

interface Emits {
  'slot-selected': [slot: TimeSlot | null]
  'date-changed': [date: Date | null]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const api = useApi()

// State
const currentMonth = ref(new Date())
const selectedDate = ref<Date | null>(null)
const selectedSlot = ref<TimeSlot | null>(null)
const availableSlots = ref<TimeSlot[]>([])
const loadingSlots = ref(false)
const error = ref<string | null>(null)
const slotAvailability = ref<Record<string, number>>({})
const slotStatus = ref<Record<string, DayStatus>>({})

// Flexible mode state
const flexInfo = ref<FlexDayInfo | null>(null)
const flexStart = ref<number | null>(null)
const flexEnd = ref<number | null>(null)

// Month availability colours: in flexible mode any free hour makes a day
// bookable, so query with a 1-hour duration
const effectiveDuration = computed(() => (props.flexible ? 1 : props.durationHours))

// Valid end hours: contiguous free hours following the chosen start
const flexEndOptions = computed<number[]>(() => {
  if (!flexInfo.value || flexStart.value === null) return []

  const free = new Set(flexInfo.value.free_hours)
  const options: number[] = []
  let hour = flexStart.value

  while (free.has(hour)) {
    options.push(hour + 1)
    hour++
  }

  return options
})

const flexTotalFormatted = computed(() => {
  if (!flexInfo.value?.hourly_rate_cents || flexStart.value === null || flexEnd.value === null) return ''
  const totalCents = flexInfo.value.hourly_rate_cents * (flexEnd.value - flexStart.value)
  return '€' + (totalCents / 100).toFixed(2).replace('.', ',')
})

// Tailwind background tints per day status (full literal strings so the JIT build keeps them)
const statusBgClass: Record<DayStatus, string> = {
  free: 'bg-green-100',
  moderate: 'bg-yellow-100',
  limited: 'bg-orange-100',
  full: 'bg-red-100',
  '': '',
}

// Colour tint for a calendar day, applied only to selectable current-month days
// that aren't selected (selection keeps the amber highlight).
const dayStatusClass = (day: CalendarDay): string => {
  if (!day.isCurrentMonth || !day.isSelectable || day.isSelected) return ''
  return statusBgClass[day.status] || ''
}

// Refs
const timeSlotsSection = ref<HTMLElement | null>(null)

// Touch gesture state
const touchStartX = ref(0)
const touchStartY = ref(0)
const touchEndX = ref(0)
const touchEndY = ref(0)
const minSwipeDistance = 50 // Minimum distance for a swipe to register

// Constants
const weekdays = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo']
const maxAdvanceDays = ref(90) // Will be loaded from backend configuration

// API is already declared above (line 164)

// Computed
const canGoToPreviousMonth = computed(() => {
  const firstOfMonth = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth(), 1)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return firstOfMonth >= today
})

const canGoToNextMonth = computed(() => {
  const nextMonth = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 1)
  const maxDate = new Date()
  maxDate.setDate(maxDate.getDate() + maxAdvanceDays.value)
  return nextMonth <= maxDate
})

const calendarDays = computed((): CalendarDay[] => {
  const year = currentMonth.value.getFullYear()
  const month = currentMonth.value.getMonth()
  
  // First day of the month
  const firstDay = new Date(year, month, 1)
  // Last day of the month
  const lastDay = new Date(year, month + 1, 0)
  
  // Start of calendar (may include previous month days)
  const startDate = new Date(firstDay)
  const startDayOfWeek = (firstDay.getDay() + 6) % 7 // Convert Sunday=0 to Monday=0
  startDate.setDate(startDate.getDate() - startDayOfWeek)
  
  // End of calendar (may include next month days)
  const endDate = new Date(lastDay)
  const endDayOfWeek = (lastDay.getDay() + 6) % 7
  endDate.setDate(endDate.getDate() + (6 - endDayOfWeek))
  
  const days: CalendarDay[] = []
  const currentDate = new Date(startDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  while (currentDate <= endDate) {
    const dateKey = formatDateKey(currentDate)
    
    days.push({
      date: new Date(currentDate),
      isCurrentMonth: currentDate.getMonth() === month,
      isToday: false, // Removed today highlighting
      isSelected: selectedDate.value?.getTime() === currentDate.getTime(),
      isSelectable: isDateSelectable(currentDate),
      availableSlots: slotAvailability.value[dateKey] || 0,
      status: slotStatus.value[dateKey] || '',
    })
    
    currentDate.setDate(currentDate.getDate() + 1)
  }
  
  return days
})

// Methods
const isDateSelectable = (date: Date): boolean => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const maxDate = new Date(today)
  maxDate.setDate(maxDate.getDate() + maxAdvanceDays.value)
  
  return date >= today && date <= maxDate
}

const formatDateKey = (date: Date): string => {
  // Use local timezone to avoid off-by-one errors
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatTime = (timeString: string): string => {
  return timeString.substring(0, 5) // Extract HH:MM from HH:MM:SS
}

const previousMonth = () => {
  if (canGoToPreviousMonth.value) {
    currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() - 1, 1)
  }
}

const nextMonth = () => {
  if (canGoToNextMonth.value) {
    currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 1)
  }
}

const selectDate = async (date: Date) => {
  if (!isDateSelectable(date)) return
  
  selectedDate.value = date
  selectedSlot.value = null
  flexStart.value = null
  flexEnd.value = null
  emit('slot-selected', null)
  emit('date-changed', date)

  if (props.flexible) {
    await loadFlexibleDayInfo(date)
  } else {
    await loadTimeSlotsForDate(date)
  }
  
  // Smooth scroll to time slots on mobile
  if (window.innerWidth < 1024 && timeSlotsSection.value) {
    setTimeout(() => {
      timeSlotsSection.value?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest' 
      })
    }, 100) // Small delay to ensure DOM is updated
  }
}

const selectTimeSlot = (slot: TimeSlot) => {
  selectedSlot.value = slot
  emit('slot-selected', slot)
  
  // Smooth scroll to next button on mobile
  if (window.innerWidth < 1024) {
    setTimeout(() => {
      // Find the next button in the parent component
      const nextButton = document.querySelector('.step-2 button.btn-primary')
      if (nextButton) {
        nextButton.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest' 
        })
      }
    }, 100)
  }
}

const loadTimeSlotsForDate = async (date: Date) => {
  if (!date) return
  
  try {
    loadingSlots.value = true
    error.value = null
    
    const dateString = formatDateKey(date)
    const response = await api.get(`/availability?date=${dateString}&duration=${props.durationHours}&customer_type=${props.customerType || 'individual'}`)
    
    if (response.success) {
      availableSlots.value = response.data?.slots || []
    } else {
      throw new Error(response.error?.message || 'Kon tijdslots niet laden')
    }
  } catch (err) {
    console.error('Error loading time slots:', err)
    error.value = 'Kon tijdslots niet laden. Probeer het opnieuw.'
    availableSlots.value = []
  } finally {
    loadingSlots.value = false
  }
}

const loadFlexibleDayInfo = async (date: Date) => {
  try {
    loadingSlots.value = true
    error.value = null

    const dateString = formatDateKey(date)
    const response = await api.get(`/availability/flexible-day?date=${dateString}&customer_type=${props.customerType || 'individual'}`)

    if (response.success) {
      flexInfo.value = (response.data || null) as FlexDayInfo | null
    } else {
      throw new Error('Kon beschikbaarheid niet laden')
    }
  } catch (err) {
    console.error('Error loading flexible day info:', err)
    error.value = 'Kon beschikbaarheid niet laden. Probeer het opnieuw.'
    flexInfo.value = null
  } finally {
    loadingSlots.value = false
  }
}

// Emit a slot once both hours are chosen so the parent form can proceed
watch([flexStart, flexEnd], () => {
  if (flexStart.value === null) {
    flexEnd.value = null
    return
  }

  // Reset an end hour that is no longer reachable from the chosen start
  if (flexEnd.value !== null && !flexEndOptions.value.includes(flexEnd.value)) {
    flexEnd.value = null
  }

  if (flexEnd.value === null || !flexInfo.value?.hourly_rate_cents) {
    emit('slot-selected', null)
    return
  }

  const hours = flexEnd.value - flexStart.value
  const totalCents = flexInfo.value.hourly_rate_cents * hours

  emit('slot-selected', {
    start_time: `${String(flexStart.value).padStart(2, '0')}:00:00`,
    end_time: `${String(flexEnd.value).padStart(2, '0')}:00:00`,
    duration_hours: hours,
    price_cents: totalCents,
    price: totalCents / 100,
    price_formatted: flexTotalFormatted.value,
    flexible: true,
  } as unknown as TimeSlot)
})

const loadMonthAvailability = async () => {
  try {
    const year = currentMonth.value.getFullYear()
    const month = currentMonth.value.getMonth() + 1 // API expects 1-based month
    
    const response = await api.get(`/availability?year=${year}&month=${month}&duration=${effectiveDuration.value}&customer_type=${props.customerType || 'individual'}`)
    
    if (response.success && response.data?.monthly_availability) {
      slotAvailability.value = response.data.monthly_availability
      // monthly_status maps date -> { available, capacity, status }; keep just the status string
      const monthlyStatus = (response.data.monthly_status || {}) as Record<string, { status: DayStatus }>
      const statusMap: Record<string, DayStatus> = {}
      for (const [dateKey, info] of Object.entries(monthlyStatus)) {
        statusMap[dateKey] = info?.status || ''
      }
      slotStatus.value = statusMap
    }
  } catch (err) {
    console.error('Error loading month availability:', err)
    // Don't show error to user - this is background data
  }
}

// Watchers
watch(currentMonth, () => {
  loadMonthAvailability()
})

watch(() => props.durationHours, () => {
  slotAvailability.value = {}
  slotStatus.value = {}
  availableSlots.value = []
  selectedSlot.value = null
  emit('slot-selected', null)
  
  loadMonthAvailability()
  
  if (selectedDate.value) {
    loadTimeSlotsForDate(selectedDate.value)
  }
})

watch(() => props.customerType, () => {
  slotAvailability.value = {}
  slotStatus.value = {}
  availableSlots.value = []
  selectedSlot.value = null
  emit('slot-selected', null)
  
  loadMonthAvailability()
  
  if (selectedDate.value) {
    loadTimeSlotsForDate(selectedDate.value)
  }
})

// Auto-select first available date
const autoSelectFirstAvailableDate = async () => {
  // Wait for availability data to load
  await loadMonthAvailability()
  
  // Small delay to ensure DOM is ready
  setTimeout(() => {
    // Find first available date in current view
    const availableDates = calendarDays.value.filter(day => 
      day.isSelectable && 
      day.availableSlots > 0 && 
      day.isCurrentMonth
    )
    
    if (availableDates.length > 0) {
      selectDate(availableDates[0].date)
    }
  }, 100)
}

// Touch gesture handlers
const handleTouchStart = (e: TouchEvent) => {
  touchStartX.value = e.touches[0].clientX
  touchStartY.value = e.touches[0].clientY
  // Initialize end to start so a tap (no move) registers zero distance
  touchEndX.value = e.touches[0].clientX
  touchEndY.value = e.touches[0].clientY
}

const handleTouchMove = (e: TouchEvent) => {
  touchEndX.value = e.touches[0].clientX
  touchEndY.value = e.touches[0].clientY
}

const handleTouchEnd = () => {
  handleSwipeGesture()
}

const handleSwipeGesture = () => {
  const horizontalDistance = touchStartX.value - touchEndX.value
  const verticalDistance = Math.abs(touchStartY.value - touchEndY.value)
  
  // Only trigger if horizontal swipe is dominant (not scrolling vertically)
  if (Math.abs(horizontalDistance) > minSwipeDistance && Math.abs(horizontalDistance) > verticalDistance) {
    if (horizontalDistance > 0) {
      // Swiped left - go to next month
      nextMonth()
    } else {
      // Swiped right - go to previous month
      previousMonth()
    }
  }
  
  // Reset values
  touchStartX.value = 0
  touchStartY.value = 0
  touchEndX.value = 0
  touchEndY.value = 0
}

// Lifecycle
onMounted(async () => {
  // Load booking configuration from public endpoint
  try {
    const response = await api.get('/config/booking')
    if (response.success && response.data?.advance_days) {
      const advanceDays = parseInt(response.data.advance_days) || 90
      maxAdvanceDays.value = advanceDays
    }
  } catch (error) {
    console.warn('Failed to load booking config, using default 90 days', error)
  }
  
  if (!(await selectInitialDate())) {
    autoSelectFirstAvailableDate()
  }
})

// Recovery link: reopen the month and date the visitor picked earlier and
// reselect their slot if it is still free. Returns false when nothing applied.
const selectInitialDate = async (): Promise<boolean> => {
  if (!props.initialDate) return false

  const [year, month, day] = props.initialDate.split('-').map(Number)
  if (!year || !month || !day) return false

  const date = new Date(year, month - 1, day)
  if (!isDateSelectable(date)) return false

  currentMonth.value = new Date(year, month - 1, 1)
  await loadMonthAvailability()
  await selectDate(date)

  if (props.initialStartTime) {
    if (props.flexible) {
      const startHour = Number(props.initialStartTime.slice(0, 2))
      if (flexInfo.value?.free_hours.includes(startHour)) {
        flexStart.value = startHour
      }
    } else {
      const slot = availableSlots.value.find(s => s.available && s.start_time === props.initialStartTime)
      if (slot) selectTimeSlot(slot)
    }
  }

  return true
}
</script>

<style scoped>
@reference "../style.css";

.booking-calendar {
  @apply w-full;
}

/* Touch gesture optimization */
.touch-pan-y {
  touch-action: pan-y;
}

/* Smooth transition for month changes */
.booking-calendar .calendar-grid {
  transition: opacity 0.2s ease-in-out;
}

/* Custom scrollbar for time slots */
.time-slots::-webkit-scrollbar {
  height: 4px;
}

.time-slots::-webkit-scrollbar-track {
  @apply bg-gray-100 rounded;
}

.time-slots::-webkit-scrollbar-thumb {
  @apply bg-gray-400 rounded;
}

.time-slots::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-500;
}
</style>